import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, LoadingIndicator, Modal } from '@asset-metrix/portal-ui';
import { Stack, Text } from '@mantine/core';

import type { DashboardModalRow } from './utils/dashboard.types';
import {
  AllDashboardsModalRow,
  type AllDashboardsRowAction,
} from './AllDashboardsModalRow';
import { DashboardDeleteConfirmation } from './DashboardDeleteConfirmation';

import styles from './dashboard-modals.module.scss';

export type AllDashboardsModalUIProps = {
  opened: boolean;
  loading?: boolean;
  instancedRows: DashboardModalRow[];
  availableRows: DashboardModalRow[];
  isDashboardOwner: (ownerUserId: number) => boolean;
  pendingActionForRow: (dashboardId: string) => AllDashboardsRowAction | null;
  actionsDisabled?: boolean;
  isMarkedForDeletion?: (dashboardId: string) => boolean;
  saveDisabled?: boolean;
  saveLoading?: boolean;
  deleteConfirmation: {
    opened: boolean;
    dashboardName: string;
    loading?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
  };
  onClose: () => void;
  onSave: () => void;
  onMoveUp: (dashboardId: string) => void;
  onMoveDown: (dashboardId: string) => void;
  onHide: (dashboardId: string) => void;
  onShow: (dashboardId: string) => void;
  onRequestDelete: (dashboardId: string, dashboardName: string) => void;
  onRestore: (dashboardId: string) => void;
};

type AllDashboardsModalSectionProps = {
  title: string;
  emptyMessage: string;
  rows: DashboardModalRow[];
  renderRow: (row: DashboardModalRow, index: number) => ReactNode;
};

function AllDashboardsModalSection({
  title,
  emptyMessage,
  rows,
  renderRow,
}: AllDashboardsModalSectionProps) {
  return (
    <Stack gap="md">
      <Text className={styles.sectionTitle} size="lg">
        {title}
      </Text>
      {rows.length === 0 ? (
        <Text c="dimmed" size="sm">
          {emptyMessage}
        </Text>
      ) : (
        <Stack gap="md">
          {rows.map((row, index) => renderRow(row, index))}
        </Stack>
      )}
    </Stack>
  );
}

export function AllDashboardsModalUI({
  opened,
  loading = false,
  instancedRows,
  availableRows,
  isDashboardOwner,
  pendingActionForRow,
  actionsDisabled,
  isMarkedForDeletion,
  saveDisabled,
  saveLoading,
  deleteConfirmation,
  onClose,
  onSave,
  onMoveUp,
  onMoveDown,
  onHide,
  onShow,
  onRequestDelete,
  onRestore,
}: AllDashboardsModalUIProps) {
  const { t } = useTranslation();

  const showLoading =
    loading && instancedRows.length === 0 && availableRows.length === 0;

  return (
    <>
      <Modal
        actions={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t('app.dashboard.allDashboardsModal.cancel')}
            </Button>
            <Button
              disabled={saveDisabled}
              loading={saveLoading}
              onClick={onSave}
            >
              {t('app.dashboard.allDashboardsModal.save')}
            </Button>
          </>
        }
        data-testid="all-dashboards-modal"
        modalType="action"
        opened={opened}
        onClose={onClose}
        size={720}
        title={t('app.dashboard.allDashboardsModal.title')}
      >
        {showLoading ? (
          <LoadingIndicator />
        ) : (
          <Stack gap="xl">
            <AllDashboardsModalSection
              emptyMessage={t(
                'app.dashboard.allDashboardsModal.instanceSectionEmpty'
              )}
              renderRow={(row, index) => (
                <AllDashboardsModalRow
                  key={row.id}
                  actionsDisabled={actionsDisabled}
                  instanceCount={instancedRows.length}
                  instanceIndex={index}
                  isOwner={isDashboardOwner(row.ownerUserId)}
                  pendingAction={pendingActionForRow(row.id)}
                  row={row}
                  variant="instanced"
                  onHide={() => onHide(row.id)}
                  onMoveDown={() => onMoveDown(row.id)}
                  onMoveUp={() => onMoveUp(row.id)}
                />
              )}
              rows={instancedRows}
              title={t('app.dashboard.allDashboardsModal.instanceSectionTitle')}
            />
            <AllDashboardsModalSection
              emptyMessage={t(
                'app.dashboard.allDashboardsModal.availableSectionEmpty'
              )}
              renderRow={(row) => (
                <AllDashboardsModalRow
                  key={row.id}
                  actionsDisabled={actionsDisabled}
                  isOwner={isDashboardOwner(row.ownerUserId)}
                  markedForDeletion={isMarkedForDeletion?.(row.id) ?? false}
                  pendingAction={pendingActionForRow(row.id)}
                  row={row}
                  variant="available"
                  onDelete={() => onRequestDelete(row.id, row.name)}
                  onRestore={() => onRestore(row.id)}
                  onShow={() => onShow(row.id)}
                />
              )}
              rows={availableRows}
              title={t(
                'app.dashboard.allDashboardsModal.availableSectionTitle'
              )}
            />
          </Stack>
        )}
      </Modal>
      <DashboardDeleteConfirmation {...deleteConfirmation} />
    </>
  );
}
